import React from 'react';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { PhoneInput } from '../PhoneInput';

export interface PhoneActivityMessageProps {
  /**
   * Current phone number value
   */
  value: string;
  /**
   * Callback when phone number changes
   */
  onChange: (value: string) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Label/aria-label for the phone input
   */
  label?: string;
  /**
   * Whether the submit button should be enabled
   */
  submitEnabled?: boolean;
  /**
   * Hide the submit button (used for locked / read-only rendering).
   */
  hideSubmit?: boolean;
  /**
   * Message content to display above the phone input
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
}

/**
 * PhoneActivityMessage displays a phone number input interface with submit button.
 * This component is designed for interactive phone number collection in chat-like interfaces,
 * with support for international phone formats and validation.
 */
export const PhoneActivityMessage: React.FC<PhoneActivityMessageProps> = ({
  value,
  onChange,
  onSubmit,
  submitText = 'Send',
  disabled = false,
  label,
  submitEnabled = true,
  hideSubmit = false,
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
}) => {
  return (
    <BaseActivityMessage
      allowSkip={false}
      submitText={submitText}
      disabled={disabled}
      submitEnabled={submitEnabled}
      hideSubmit={hideSubmit}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      onSubmit={onSubmit}
      renderInput={() => (
        <PhoneInput value={value} ariaLabel={label} disabled={disabled} onChange={onChange} size="small" />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
